import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  Save,
  RefreshCw,
  Megaphone,
  BookOpen,
  Plus,
  Trash2,
  Pin,
  PinOff,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import BriefingManagerTab from './BriefingManagerTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TouchEditor = ({ touchKey, label, config, onChange }) => {
  const t = config?.[touchKey] || {
    headline: '',
    subheadline: '',
    video_url: '',
    body_text: '',
    cta_button_text: '',
  };
  const set = (field, value) =>
    onChange({ ...config, [touchKey]: { ...t, [field]: value } });

  return (
    <Card className="border border-slate-200">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">{label}</h3>
          <Badge variant="outline">{touchKey}</Badge>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Headline</label>
          <Input
            value={t.headline}
            onChange={(e) => set('headline', e.target.value)}
            data-testid={`admin-${touchKey}-headline`}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Subheadline</label>
          <Input
            value={t.subheadline}
            onChange={(e) => set('subheadline', e.target.value)}
            data-testid={`admin-${touchKey}-sub`}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Video URL</label>
          <Input
            value={t.video_url}
            onChange={(e) => set('video_url', e.target.value)}
            placeholder="Bunny: https://player.mediadelivery.net/embed/… (also supports YouTube/Vimeo/.mp4)"
            data-testid={`admin-${touchKey}-video`}
          />
          <p className="text-[10px] text-slate-500 mt-1">
            Paste the Bunny Stream embed URL. Recommended query params:{' '}
            <code>?autoplay=false&loop=false&muted=false&preload=true&responsive=true</code>
          </p>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Body text</label>
          <Textarea
            rows={4}
            value={t.body_text}
            onChange={(e) => set('body_text', e.target.value)}
            data-testid={`admin-${touchKey}-body`}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">CTA button text</label>
          <Input
            value={t.cta_button_text}
            onChange={(e) => set('cta_button_text', e.target.value)}
            data-testid={`admin-${touchKey}-cta`}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const FeedManager = ({ kind, title, icon: Icon }) => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: '',
    body: '',
    video_url: '',
    pinned: false,
    is_published: true,
  });

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/waitlist/feed?item_type=${kind}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.ok) setItems(json.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [kind]);

  const handleCreate = async () => {
    if (!form.title.trim()) {
      toast.error('Title required');
      return;
    }
    const res = await fetch(`${BACKEND_URL}/api/admin/waitlist/feed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        item_type: kind,
        title: form.title,
        body: form.body,
        video_url: form.video_url.trim() || null,
        pinned: form.pinned,
        is_published: form.is_published,
      }),
    });
    const json = await res.json();
    if (json.ok) {
      toast.success(`${title} created`);
      setForm({ title: '', body: '', video_url: '', pinned: false, is_published: true });
      fetchItems();
    } else {
      toast.error(json.detail || 'Failed to create');
    }
  };

  const handleTogglePinned = async (item) => {
    await fetch(`${BACKEND_URL}/api/admin/waitlist/feed/${item.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ pinned: !item.pinned }),
    });
    fetchItems();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await fetch(`${BACKEND_URL}/api/admin/waitlist/feed/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    toast.success('Deleted');
    fetchItems();
  };

  const handleCleanupTestSeed = async () => {
    if (!window.confirm('Delete known test/demo seed items (e.g. "Phase 2 unlock / Demo Member has unlocked Phase 2.")? Real admin posts will not be touched.')) return;
    const res = await fetch(`${BACKEND_URL}/api/admin/waitlist/feed/cleanup-test-seed`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (json.ok) {
      toast.success(`Removed ${json.deleted} test/demo item${json.deleted === 1 ? '' : 's'}`);
      fetchItems();
    } else {
      toast.error(json.detail || 'Cleanup failed');
    }
  };

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-amber-600" />
            <h3 className="font-semibold text-slate-800">{title}</h3>
          </div>
          {kind === 'announcement' && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleCleanupTestSeed}
              data-testid="cleanup-test-seed-btn"
              className="text-xs"
            >
              <Trash2 className="h-3 w-3 mr-1 text-red-500" />
              Delete test/demo announcements
            </Button>
          )}
        </div>

        {/* Create form */}
        <div className="rounded-lg border border-slate-200 p-3 space-y-2 bg-slate-50">
          <Input
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid={`admin-${kind}-title-input`}
          />
          <Textarea
            placeholder="Body"
            rows={3}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />
          <Input
            placeholder="Video URL (optional)"
            value={form.video_url}
            onChange={(e) => setForm({ ...form, video_url: e.target.value })}
          />
          <div className="flex items-center gap-3 text-sm">
            <label className="flex items-center gap-1">
              <Switch checked={form.pinned} onCheckedChange={(v) => setForm({ ...form, pinned: v })} /> Pinned
            </label>
            <label className="flex items-center gap-1">
              <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} />
              Published
            </label>
            <Button
              size="sm"
              className="ml-auto"
              onClick={handleCreate}
              data-testid={`admin-${kind}-create-btn`}
            >
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500">No items.</p>
        ) : (
          <ul className="space-y-2">
            {items.map((it) => (
              <li key={it.id} className="rounded-lg border p-3 flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    {it.pinned && <Pin className="h-3 w-3 text-amber-600" />}
                    <p className="font-medium text-slate-800">{it.title}</p>
                    {!it.is_published && (
                      <Badge variant="outline" className="text-xs">Draft</Badge>
                    )}
                  </div>
                  {it.body && (
                    <p className="text-sm text-slate-600 whitespace-pre-line mt-1">{it.body}</p>
                  )}
                  {it.video_url && (
                    <a
                      href={it.video_url}
                      className="text-xs text-amber-600 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {it.video_url}
                    </a>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleTogglePinned(it)}
                    title={it.pinned ? 'Unpin' : 'Pin'}
                  >
                    {it.pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(it.id)}
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

const SignupsTab = () => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const q = filter ? `?queue_state=${filter}` : '';
      const [signupsRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/waitlist/signups${q}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${BACKEND_URL}/api/admin/waitlist/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const s = await signupsRes.json();
      const st = await statsRes.json();
      if (s.ok) setItems(s.items);
      if (st.ok) setStats(st.stats);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const handleProcessQueue = async () => {
    const res = await fetch(`${BACKEND_URL}/api/admin/waitlist/process-queue`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (json.ok) {
      toast.success(
        `Processed: ${json.result.personal_to_network} → network, ${json.result.network_to_global} → global`
      );
      fetchData();
    }
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {stats &&
          ['personal', 'network', 'global', 'claimed', 'activated'].map((s) => (
            <Card key={s}>
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wider text-slate-500">{s}</p>
                <p className="text-2xl font-bold text-slate-800">
                  {stats.by_queue_state?.[s] ?? 0}
                </p>
              </CardContent>
            </Card>
          ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm"
          data-testid="admin-signups-filter"
        >
          <option value="">All queue states</option>
          <option value="personal">Personal</option>
          <option value="network">Network</option>
          <option value="global">Global</option>
          <option value="claimed">Claimed</option>
          <option value="activated">Activated</option>
        </select>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
        <Button
          size="sm"
          onClick={handleProcessQueue}
          data-testid="admin-process-queue-btn"
        >
          Process queue transitions
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-slate-500">No signups yet.</CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left">
                <th className="p-3">Name</th>
                <th className="p-3">Email</th>
                <th className="p-3">Phone</th>
                <th className="p-3">Source</th>
                <th className="p-3">Queue</th>
                <th className="p-3">Submitted</th>
                <th className="p-3">Wallet</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t">
                  <td className="p-3 font-medium">{s.first_name} {s.last_name}</td>
                  <td className="p-3">{s.email}</td>
                  <td className="p-3">{s.mobile_phone}</td>
                  <td className="p-3">
                    <Badge variant="outline">{s.source}</Badge>
                  </td>
                  <td className="p-3">
                    <Badge>{s.queue_state}</Badge>
                  </td>
                  <td className="p-3 text-xs text-slate-500">
                    {s.submitted_at ? new Date(s.submitted_at).toLocaleString() : '—'}
                  </td>
                  <td className="p-3 text-xs">
                    {s.wallet_trc20
                      ? <span className="text-emerald-600">on file</span>
                      : (s.needs_wallet_help
                          ? <span className="text-amber-600">needs help</span>
                          : <span className="text-slate-400">missing</span>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AdminWaitlistConfig = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/waitlist/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.ok) setConfig(json.config);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      // Drop fields that backend doesn't expect on input
      const payload = { ...config };
      delete payload.id;
      delete payload.updated_at;
      delete payload.updated_by;
      const res = await fetch(`${BACKEND_URL}/api/admin/waitlist/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json.ok) {
        toast.success('Waitlist config saved');
        setConfig(json.config);
      } else {
        toast.error(json.detail || 'Failed to save');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return <div className="p-6 text-slate-500">Loading…</div>;
  }

  const set = (field, value) => setConfig({ ...config, [field]: value });

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Waitlist Launch Funnel</h1>
          <p className="text-sm text-slate-500">
            Edit the Four Touch content, queue timers, and pre-launch back-office feed.
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="admin-waitlist-save">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>

      <Tabs defaultValue="content">
        <TabsList>
          <TabsTrigger value="content">Funnel content</TabsTrigger>
          <TabsTrigger value="form">Form & timers</TabsTrigger>
          <TabsTrigger value="briefing">Activation briefing</TabsTrigger>
          <TabsTrigger value="signups">
            <Users className="h-4 w-4 mr-1" /> Signups
          </TabsTrigger>
          <TabsTrigger value="announcements">
            <Megaphone className="h-4 w-4 mr-1" /> Announcements
          </TabsTrigger>
          <TabsTrigger value="training">
            <BookOpen className="h-4 w-4 mr-1" /> Training
          </TabsTrigger>
        </TabsList>

        {/* Funnel content */}
        <TabsContent value="content" className="space-y-4 pt-4">
          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="font-semibold text-slate-800">Master toggles</h3>
              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={!!config.waitlist_enabled}
                    onCheckedChange={(v) => set('waitlist_enabled', v)}
                    data-testid="admin-waitlist-toggle"
                  />
                  Waiting list ON
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={!!config.full_access_enabled}
                    onCheckedChange={(v) => set('full_access_enabled', v)}
                    data-testid="admin-fullaccess-toggle"
                  />
                  Full access activated (launch live)
                </label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="font-semibold text-slate-800">Hero</h3>
              <div>
                <label className="text-xs font-medium text-slate-600">Eyebrow</label>
                <Input
                  value={config.hero_eyebrow || ''}
                  onChange={(e) => set('hero_eyebrow', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Headline</label>
                <Input
                  value={config.hero_headline || ''}
                  onChange={(e) => set('hero_headline', e.target.value)}
                  data-testid="admin-hero-headline"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Subheadline</label>
                <Textarea
                  rows={2}
                  value={config.hero_subheadline || ''}
                  onChange={(e) => set('hero_subheadline', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <TouchEditor touchKey="touch_2" label="Touch 2 — The Problem" config={config} onChange={setConfig} />
          <TouchEditor touchKey="touch_3" label="Touch 3 — The System" config={config} onChange={setConfig} />
          <TouchEditor touchKey="touch_4" label="Touch 4 — Your Position" config={config} onChange={setConfig} />
        </TabsContent>

        {/* Form & timers */}
        <TabsContent value="form" className="space-y-4 pt-4">
          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="font-semibold text-slate-800">Form copy</h3>
              <div>
                <label className="text-xs font-medium text-slate-600">Form title</label>
                <Input
                  value={config.form_title || ''}
                  onChange={(e) => set('form_title', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Consent checkbox label</label>
                <Input
                  value={config.consent_label || ''}
                  onChange={(e) => set('consent_label', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Wallet help label</label>
                <Input
                  value={config.wallet_help_label || ''}
                  onChange={(e) => set('wallet_help_label', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Disclaimer text</label>
                <Textarea
                  rows={3}
                  value={config.disclaimer_text || ''}
                  onChange={(e) => set('disclaimer_text', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="font-semibold text-slate-800">Thank-you screen</h3>
              <div>
                <label className="text-xs font-medium text-slate-600">Headline</label>
                <Input
                  value={config.thank_you_headline || ''}
                  onChange={(e) => set('thank_you_headline', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Body</label>
                <Textarea
                  rows={3}
                  value={config.thank_you_body || ''}
                  onChange={(e) => set('thank_you_body', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="font-semibold text-slate-800">Pay-It-Forward queue timers</h3>
              <p className="text-xs text-slate-500">
                Hours a prospect stays in each queue before automatically releasing to the next layer.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Personal → Network (hours)</label>
                  <Input
                    type="number"
                    min={1}
                    max={720}
                    value={config.personal_window_hours ?? 24}
                    onChange={(e) => set('personal_window_hours', parseInt(e.target.value || '0', 10))}
                    data-testid="admin-personal-hours"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Network → Global (hours)</label>
                  <Input
                    type="number"
                    min={1}
                    max={720}
                    value={config.network_window_hours ?? 48}
                    onChange={(e) => set('network_window_hours', parseInt(e.target.value || '0', 10))}
                    data-testid="admin-network-hours"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activation briefing */}
        <TabsContent value="briefing" className="space-y-4 pt-4">
          <BriefingManagerTab />
        </TabsContent>

        <TabsContent value="signups" className="pt-4">
          <SignupsTab />
        </TabsContent>

        <TabsContent value="announcements" className="pt-4">
          <FeedManager kind="announcement" title="Admin announcements" icon={Megaphone} />
        </TabsContent>

        <TabsContent value="training" className="pt-4">
          <FeedManager kind="training" title="Training & updates" icon={BookOpen} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminWaitlistConfig;
